import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ButtonBase,
  Checkbox,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { LoadingButton } from '@mui/lab';
import { AddCircleOutline, Delete, ExpandLess, ExpandMore, RemoveCircleOutline, Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  AccountData,
  CatalogItemOut,
  ExtraItemInput,
  ExtraItemLineData,
  OrderData,
  OrderService,
} from '../../../../../api';
import { ApiError } from '../../../../../api/core/ApiError';
import { ColorButton } from '../../../../../common/ui/ConfimationDialog';
import { useSafeExecute } from '../../../../../common/apiNotifications/safeExecute';
import RotatingLoader from '../../../../../common/ui/RotatingLoader';
import { MultiSelectFilter } from '../../../../../common/ui/MultiSelectFilter';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { CatalogItemPickerCard } from './CatalogItemPickerCard';
import { BODY_WEIGHT } from '../../../../agendaView/agendaStyles';
import { localizedLabel } from '../../../../../utils/maskUtils';
import { getCurrencySymbol, priceFormatterFromCurrency } from '../../../../../utils/utils';
import { useCatalogCategories } from '../../../../../catalog/swr/useCatalogCategories';
import { useCatalogItems } from '../../../../../catalog/swr/useCatalogItems';
import { getOrderPages } from '../../../../../utils/AccountUtils';
import {
  collectExistingTags,
  defaultPickerOrderPageId,
  filterCatalogItemsForPicker,
  pickerOrderPageIds,
  visibleCatalogCategories,
} from '../../../../../catalog/catalogUtils';

const T_PREFIX = 'calendar.edit_order.order_details.extraItems';

const QUICK_QUANTITIES = [1, 2, 3];
const CATALOG_TAB = 'catalog';
const CUSTOM_TAB = 'custom';

type CartLine = {
  key: string;
  lineId?: string;
  quantity: number;
  unitPrice: number;
  catalogItem?: CatalogItemOut;
  customLabel?: string;
  customPrice?: number;
};

type AddExtraItemDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  orderPageId?: string;
  existingLines: ExtraItemLineData[];
  onCommitted: (freshOrder: OrderData) => void;
};

export const AddExtraItemDialog = ({
  isOpen,
  onClose,
  orderId,
  orderPageId,
  existingLines,
  onCommitted,
}: AddExtraItemDialogProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const language = useLanguage();
  const theme = useTheme();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const currencySymbol = getCurrencySymbol(account);
  const { categories } = useCatalogCategories();
  const { items, isLoading } = useCatalogItems();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [isCartExpanded, setIsCartExpanded] = useState(false);
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [cart, setCart] = useState<Map<string, CartLine>>(new Map());
  const [seededLines, setSeededLines] = useState<CartLine[]>([]);
  const [otherDrafts, setOtherDrafts] = useState<Record<string, string>>({});
  const isSeededForThisOpen = useRef(false);
  const customLineCounter = useRef(0);
  const [customLabel, setCustomLabel] = useState('');
  const [customPrice, setCustomPrice] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const orderPages = useMemo(() => getOrderPages(account), [account]);
  const pageTabIds = useMemo(
    () =>
      pickerOrderPageIds(
        items,
        categories,
        orderPages.map((page) => page.id),
      ),
    [items, categories, orderPages],
  );

  // Opens on the order's own workshop once the catalog is loaded; without workshop tabs, on the whole catalog.
  useEffect(() => {
    if (isOpen && !isLoading && activeTab === null) {
      setActiveTab(defaultPickerOrderPageId(pageTabIds, orderPageId) ?? CATALOG_TAB);
    }
  }, [isOpen, isLoading, activeTab, pageTabIds, orderPageId]);

  const browsedPageId = activeTab && activeTab !== CATALOG_TAB && activeTab !== CUSTOM_TAB ? activeTab : undefined;

  const visibleCategories = useMemo(
    () =>
      browsedPageId
        ? visibleCatalogCategories(categories, browsedPageId, false, language)
        : visibleCatalogCategories(categories, orderPageId, showAllCategories, language),
    [categories, browsedPageId, orderPageId, showAllCategories, language],
  );

  const categoryLabelById = useMemo(
    () => new Map(categories.map((category) => [category.id, localizedLabel(category.label, language)])),
    [categories, language],
  );

  const existingTags = useMemo(() => collectExistingTags(items), [items]);

  const visibleItems = useMemo(
    () =>
      filterCatalogItemsForPicker(
        items,
        visibleCategories,
        { categoryIds: categoryFilters, tags: tagFilters, search },
        language,
      ),
    [items, visibleCategories, categoryFilters, tagFilters, search, language],
  );

  const buildSeedFromLines = (lines: ExtraItemLineData[]): CartLine[] => {
    const itemById = new Map(items.map((item) => [item.id, item]));
    return lines.map((line): CartLine => {
      const catalogItem = line.catalog_item_id ? itemById.get(line.catalog_item_id) : undefined;
      return catalogItem
        ? { key: line.id, lineId: line.id, quantity: line.quantity, unitPrice: line.unit_price, catalogItem }
        : {
            key: line.id,
            lineId: line.id,
            quantity: line.quantity,
            unitPrice: line.unit_price,
            customLabel: line.label,
            customPrice: line.unit_price,
          };
    });
  };

  useEffect(() => {
    if (!isOpen) {
      isSeededForThisOpen.current = false;
      return;
    }
    if (isSeededForThisOpen.current || isLoading) {
      return;
    }
    isSeededForThisOpen.current = true;
    const lines = buildSeedFromLines(existingLines);
    setSeededLines(lines);
    setCart((current) => new Map([...lines.map((line): [string, CartLine] => [line.key, line]), ...current]));
  }, [isOpen, isLoading, items, existingLines]);

  const cartKeyByCatalogItemId = useMemo(
    () =>
      new Map(seededLines.filter((line) => line.catalogItem).map((line) => [line.catalogItem?.id as string, line.key])),
    [seededLines],
  );

  const isCustomMode = activeTab === CUSTOM_TAB;
  const canAddCustomLine = !!customLabel.trim() && customPrice !== '' && Number(customPrice) >= 0;
  const cartLines = Array.from(cart.values());
  const hasSeededLines = seededLines.length > 0;
  const removedLines = seededLines.filter((line) => !cart.has(line.key));
  const changedLines = cartLines.filter(
    (line) => line.lineId && line.quantity !== seededLines.find((seededLine) => seededLine.key === line.key)?.quantity,
  );
  const newLines = cartLines.filter((line) => !line.lineId);
  const hasCartChanges = !!removedLines.length || !!changedLines.length || !!newLines.length;
  const cartTotal = cartLines.reduce((total, line) => total + line.unitPrice * line.quantity, 0);

  const resetAndClose = () => {
    setActiveTab(null);
    setSearch('');
    setCategoryFilters([]);
    setTagFilters([]);
    setCart(new Map());
    setSeededLines([]);
    setOtherDrafts({});
    setCustomLabel('');
    setCustomPrice('');
    setIsCartExpanded(false);
    onClose();
  };

  const changeCartQuantity = (key: string, quantity: number) => {
    setOtherDrafts(({ [key]: _discardedDraft, ...remainingDrafts }) => remainingDrafts);
    setCart((current) => {
      const next = new Map(current);
      const line = next.get(key);
      if (!line) {
        return current;
      }
      if (quantity < 1) {
        next.delete(key);
      } else {
        next.set(key, { ...line, quantity });
      }
      return next;
    });
  };

  const toggleCatalogItemInCart = (item: CatalogItemOut) =>
    setCart((current) => {
      const next = new Map(current);
      const key = cartKeyByCatalogItemId.get(item.id) ?? item.id;
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      next.set(key, {
        key,
        lineId: seededLines.find((seededLine) => seededLine.key === key)?.lineId,
        catalogItem: item,
        quantity: 1,
        unitPrice: seededLines.find((seededLine) => seededLine.key === key)?.unitPrice ?? item.price,
      });
      return next;
    });

  const addCustomLineToCart = () => {
    customLineCounter.current += 1;
    const key = `custom-${customLineCounter.current}`;
    setCart((current) =>
      new Map(current).set(key, {
        key,
        quantity: 1,
        unitPrice: Number(customPrice),
        customLabel: customLabel.trim(),
        customPrice: Number(customPrice),
      }),
    );
    setCustomLabel('');
    setCustomPrice('');
  };

  const onErrorMessageGenerator = (error: ApiError) =>
    // FastAPI sends HTTPException(detail=...) as {"detail": ...}, which safeExecute's
    // default (error.body?.message) never matches.
    typeof error.body?.detail === 'string' ? error.body.detail : t(`${T_PREFIX}.errorGeneric`);

  const submitCart = async () => {
    setIsSubmitting(true);
    let latestOrder: OrderData | undefined;
    try {
      for (const line of removedLines) {
        latestOrder = await safeExecute({
          apiCall: () => OrderService.removeOrderExtraItem(orderId, line.lineId as string),
          successMessage: '',
          suppressSuccessMessage: true,
          onErrorMessageGenerator,
        });
        setSeededLines(buildSeedFromLines(latestOrder.extra_items ?? []));
      }
      for (const line of changedLines) {
        latestOrder = await safeExecute({
          apiCall: () =>
            OrderService.updateOrderExtraItem({
              order_id: orderId,
              item_line_id: line.lineId as string,
              quantity: line.quantity,
            }),
          successMessage: '',
          suppressSuccessMessage: true,
          onErrorMessageGenerator,
        });
        setSeededLines(buildSeedFromLines(latestOrder.extra_items ?? []));
      }
      if (newLines.length) {
        // Each line takes either a catalog_item_id or a custom label + price, never both.
        const extraItems: ExtraItemInput[] = newLines.map((line) =>
          line.catalogItem
            ? { catalog_item_id: line.catalogItem.id, quantity: line.quantity }
            : { label: line.customLabel, price: line.customPrice, quantity: line.quantity },
        );
        latestOrder = await safeExecute({
          apiCall: () => OrderService.addOrderExtraItems({ order_id: orderId, items: extraItems }),
          successMessage: t(`${T_PREFIX}.itemsAdded`),
          onErrorMessageGenerator,
        });
        setSeededLines(buildSeedFromLines(latestOrder.extra_items ?? []));
      }
      if (latestOrder) {
        onCommitted(latestOrder);
      }
      resetAndClose();
    } catch {
      // safeExecute already surfaced the toast; keep the dialog open with the cart intact.
      if (latestOrder) {
        onCommitted(latestOrder);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuantityPicker = (line: CartLine) => {
    if (!isWideScreen) {
      return (
        <Grid item container width="auto" alignItems="center" flexWrap="nowrap">
          <IconButton
            size="small"
            disabled={isSubmitting}
            onClick={() => changeCartQuantity(line.key, line.quantity - 1)}>
            <RemoveCircleOutline fontSize="small" />
          </IconButton>
          <Typography variant="body2" dir="ltr" fontWeight={600} minWidth="20px" textAlign="center">
            {line.quantity}
          </Typography>
          <IconButton
            size="small"
            disabled={isSubmitting}
            onClick={() => changeCartQuantity(line.key, line.quantity + 1)}>
            <AddCircleOutline fontSize="small" />
          </IconButton>
        </Grid>
      );
    }
    const draft = otherDrafts[line.key];
    const isQuickQuantity = QUICK_QUANTITIES.includes(line.quantity) && draft === undefined;
    const commitDraft = () => {
      if (draft !== undefined) {
        changeCartQuantity(line.key, Math.max(1, Number(draft) || 1));
      }
    };
    return (
      <Grid item container width="auto" alignItems="center" gap={0.5} flexWrap="nowrap">
        <ToggleButtonGroup
          exclusive
          size="small"
          disabled={isSubmitting}
          value={isQuickQuantity ? line.quantity : null}
          onChange={(_, quantity: number | null) => quantity && changeCartQuantity(line.key, quantity)}>
          {QUICK_QUANTITIES.map((quickQuantity) => (
            <ToggleButton
              key={quickQuantity}
              value={quickQuantity}
              sx={{ px: 1.25, py: 0.25, minWidth: '40px', fontSize: '1.05rem', fontWeight: 600 }}>
              {quickQuantity}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          type="number"
          disabled={isSubmitting}
          sx={{ width: '76px' }}
          value={draft ?? (isQuickQuantity ? '' : line.quantity)}
          placeholder={t(`${T_PREFIX}.otherQuantity`)}
          onChange={(event) => setOtherDrafts((current) => ({ ...current, [line.key]: event.target.value }))}
          onBlur={commitDraft}
          onKeyDown={(event) => event.key === 'Enter' && commitDraft()}
          InputProps={{
            inputProps: { min: 1, dir: 'ltr', style: { padding: '6px 8px', fontSize: '1.05rem', fontWeight: 600 } },
          }}
        />
      </Grid>
    );
  };

  const renderCartLines = () => (
    <Grid
      container
      flexDirection="column"
      flexWrap="nowrap"
      gap={1}
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.5 }}>
      {cartLines.map((line) => (
        <Grid
          key={line.key}
          container
          flexDirection="column"
          flexWrap="nowrap"
          gap={0.75}
          sx={{ paddingBlock: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography fontWeight={500} sx={{ overflowWrap: 'anywhere' }}>
            {line.catalogItem ? localizedLabel(line.catalogItem.name, language) : line.customLabel}
          </Typography>
          <Grid container alignItems="center" gap={1}>
            {renderQuantityPicker(line)}
            <Typography variant="body2" dir="ltr" sx={{ marginInlineStart: 'auto' }}>
              {priceFormatterFromCurrency(line.unitPrice * line.quantity, account.currency, undefined, false, false)}
            </Typography>
            <IconButton size="small" disabled={isSubmitting} onClick={() => changeCartQuantity(line.key, 0)}>
              <Delete fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );

  const renderCartTotal = () => (
    <Grid container justifyContent="space-between" mt={1} pt={1} sx={{ borderTop: 1, borderColor: 'divider' }}>
      <Typography fontWeight="700">{t(`${T_PREFIX}.cartTotal`)}</Typography>
      <Typography fontWeight="700" dir="ltr">
        {priceFormatterFromCurrency(cartTotal, account.currency, undefined, false, false)}
      </Typography>
    </Grid>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={resetAndClose}
      fullScreen
      sx={{
        '@media (min-width: 1200px)': {
          'body:has(.titled-drawer) &': {
            '& .MuiDialog-container': { paddingRight: 0 },
          },
        },
      }}
      PaperProps={{
        sx: {
          '& .MuiDialogTitle-root': { fontSize: '24px', fontWeight: 700 },
          '& .MuiTab-root': { fontSize: '18px', fontWeight: 600 },
          '& :where(.MuiTypography-root)': { fontSize: '18px', fontWeight: BODY_WEIGHT },
          '& :where(.MuiTypography-body2, .MuiTypography-caption)': { fontSize: '17px', fontWeight: BODY_WEIGHT },
          '& .MuiInputBase-root, & .MuiInputBase-input, & .MuiInputLabel-root, & .MuiSelect-select, & .MuiMenuItem-root':
            { fontSize: '18px' },
          '& .MuiToggleButton-root': { fontSize: '18px', minWidth: '44px' },
          '& .MuiFormControlLabel-label': { fontSize: '17px' },
          '& .MuiChip-label': { fontSize: '16px' },
          '& .MuiButton-root': { fontSize: '18px' },
        },
      }}>
      <DialogTitle>{t(`${T_PREFIX}.addTitle`)}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'row', gap: 3, overflow: 'hidden' }}>
        <Grid container flexDirection="column" flexWrap="nowrap" flex={1} minWidth={0} minHeight={0}>
          <Tabs
            value={activeTab ?? false}
            onChange={(_, newTab: string) => {
              setActiveTab(newTab);
              setCategoryFilters([]);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 1 }}>
            {pageTabIds.map((pageId) => (
              <Tab key={pageId} value={pageId} label={orderPages.find((page) => page.id === pageId)?.label[language]} />
            ))}
            {!pageTabIds.length && <Tab value={CATALOG_TAB} label={t(`${T_PREFIX}.fromCatalog`)} />}
            <Tab value={CUSTOM_TAB} label={t(`${T_PREFIX}.customLine`)} />
          </Tabs>
          {isCustomMode ? (
            <Grid container flexDirection="column" flexWrap="nowrap" gap={2} mt={1} maxWidth="250px">
              <TextField
                size="small"
                label={t(`${T_PREFIX}.itemLabel`)}
                value={customLabel}
                onChange={(event) => setCustomLabel(event.target.value)}
              />
              <TextField
                size="small"
                type="number"
                label={t('calendar.edit_order.priceWord')}
                value={customPrice}
                onChange={(event) => setCustomPrice(event.target.value === '' ? '' : Number(event.target.value))}
                InputProps={{ endAdornment: currencySymbol, inputProps: { min: 0, dir: 'ltr' } }}
              />
              <ColorButton variant="outlined" disabled={!canAddCustomLine} onClick={addCustomLineToCart}>
                {t(`${T_PREFIX}.addToCart`)}
              </ColorButton>
            </Grid>
          ) : (
            <Grid container flexDirection="column" flexWrap="nowrap" gap={1} flex={1} minHeight={0}>
              <Grid item container gap={1} alignItems="center">
                <TextField
                  size="small"
                  sx={{ flex: 1, minWidth: '160px' }}
                  placeholder={t('search')}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <MultiSelectFilter
                  label={t('catalog.category')}
                  emptyLabel={t('catalog.allCategories')}
                  minWidth="150px"
                  value={categoryFilters}
                  onChange={setCategoryFilters}
                  options={visibleCategories.map((category) => ({
                    value: category.id,
                    label: localizedLabel(category.label, language),
                  }))}
                />
                {!!existingTags.length && (
                  <MultiSelectFilter
                    label={t('catalog.tags')}
                    emptyLabel={t('catalog.allTags')}
                    minWidth="140px"
                    value={tagFilters}
                    onChange={setTagFilters}
                    options={existingTags.map((tag) => ({ value: tag, label: tag }))}
                  />
                )}
              </Grid>
              {activeTab === CATALOG_TAB && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showAllCategories}
                      onChange={(event) => setShowAllCategories(event.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">{t(`${T_PREFIX}.showAllCategories`)}</Typography>}
                />
              )}
              {isLoading ? (
                <RotatingLoader width={'24px'} />
              ) : (
                <Grid
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    display: 'grid',
                    gridTemplateColumns: `repeat(auto-fill, minmax(${isWideScreen ? 170 : 104}px, 1fr))`,
                    gridAutoRows: 'max-content',
                    gap: 1,
                    alignContent: 'start',
                    paddingBlock: 0.5,
                  }}>
                  {visibleItems.map((item) => {
                    const cartLine = cart.get(cartKeyByCatalogItemId.get(item.id) ?? item.id);
                    return (
                      <CatalogItemPickerCard
                        key={item.id}
                        item={item}
                        categoryLabel={categoryLabelById.get(item.category_id)}
                        isSelected={!!cartLine}
                        quantity={cartLine?.quantity}
                        currency={account.currency}
                        language={language}
                        onToggle={() => toggleCatalogItemInCart(item)}
                      />
                    );
                  })}
                  {!visibleItems.length && (
                    <Typography variant="body2" color="text.secondary" p={1} sx={{ gridColumn: '1 / -1' }}>
                      {t(`${T_PREFIX}.noItemsFound`)}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
        {isWideScreen && (
          <Grid
            container
            flexDirection="column"
            flexWrap="nowrap"
            sx={{
              width: 460,
              flexShrink: 0,
              minHeight: 0,
              borderInlineStart: 1,
              borderColor: 'divider',
              paddingInlineStart: 3,
            }}>
            <Typography fontWeight="700" mb={1}>
              {t(`${T_PREFIX}.cart`)}
              {!!cartLines.length && ` (${cartLines.length})`}
            </Typography>
            {cartLines.length ? (
              renderCartLines()
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t(`${T_PREFIX}.cartEmpty`)}
              </Typography>
            )}
            {!!cartLines.length && renderCartTotal()}
          </Grid>
        )}
      </DialogContent>
      {!isWideScreen && !!cartLines.length && (
        <Grid container flexDirection="column" flexWrap="nowrap" px={2} pb={1}>
          <Divider />
          <ButtonBase
            onClick={() => setIsCartExpanded((current) => !current)}
            aria-expanded={isCartExpanded}
            sx={{ justifyContent: 'space-between', paddingBlock: 1, gap: 1 }}>
            <Typography fontWeight="700">{`${t(`${T_PREFIX}.cart`)} (${cartLines.length})`}</Typography>
            <Grid container alignItems="center" gap={0.5} width="auto" flexWrap="nowrap">
              <Typography fontWeight="700" dir="ltr">
                {priceFormatterFromCurrency(cartTotal, account.currency, undefined, false, false)}
              </Typography>
              {isCartExpanded ? <ExpandMore /> : <ExpandLess />}
            </Grid>
          </ButtonBase>
          {isCartExpanded && (
            <Grid sx={{ maxHeight: '45vh', display: 'flex', flexDirection: 'column' }}>{renderCartLines()}</Grid>
          )}
        </Grid>
      )}
      <DialogActions>
        <ColorButton onClick={resetAndClose}>{t('cancel')}</ColorButton>
        <LoadingButton
          loading={isSubmitting}
          disabled={!hasCartChanges}
          variant="contained"
          onClick={submitCart}
          sx={{ boxShadow: 'none' }}>
          {hasSeededLines ? t(`${T_PREFIX}.saveCart`) : t(`${T_PREFIX}.addAll`, { count: cartLines.length })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
